import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import LowPriorityIcon from '@material-ui/icons/LowPriority';
import DepartureBoardIcon from '@material-ui/icons/DepartureBoard';
import SaveIcon from '@material-ui/icons/Save';
import PhoneIcon from '@material-ui/icons/PhonelinkRing';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNotify } from 'react-admin';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { apiGet, apiPost } from '../brainApi';
import { DispatchContext } from '../Context';
import DragContainer, { ContainerFooter } from './DragContainer';
import EtaSmsModal from './EtaSmsModal';
import { getZ3PConfigValue } from '../helper';

const useStyles = makeStyles((theme) => ({
  secondaryLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginLeft: 20,
  },
  serviceWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    overflow: 'auto',
  },
  fullWidth: {
    width: '100%',
  },
  accordionLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  actionButton: {
    marginLeft: 10,
    minWidth: 70,
  },
  actionSection: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
  serviceHeader: {
    padding: '0 16px',
    background: '#f9f9f9',
  },
}));

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const DispatchVehicle = ({ data }) => {
  const notify = useNotify();
  const {
    mapData,
    setMapData,
    setReOrdered,
    isEditing,
    setIsEditing,
  } = useContext(DispatchContext);

  const { service_vehicle_id, service_vehicle_name, schedule_instance_id } =
    data || {};
  const classes = useStyles();
  const [orgStops, setOrgStops] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [totalMiles, setTotalMiles] = useState(0);
  const [totalServiceHours, setTotalServiceHours] = useState(0);
  const [routeWasSetManually, setRouteWasSetManually] = useState(false);
  const [travelMinutes, setTravelMinutes] = useState([]);
  const [travelMiles, setTravelMiles] = useState([]);
  const [reorderedStops, setReorderedStops] = useState([]);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEtaSmsModal, setShowEtaSmsModal] = useState(false);
  const prevReorderedStops = useRef(null);

  useEffect(() => {
    if (data) {
      setIsEditing(false);
    }
  }, [data]);

  const toggleIsEditing = () => {
    setIsEditing((prevState) => !prevState);
  };

  const reset = useCallback(() => {
    setOrgStops([]);
    setTotalMinutes(0);
    setTotalMiles(0);
    setTravelMinutes([]);
    setTravelMiles([]);
    setTotalServiceHours(0);
    setRouteWasSetManually(false);
    setLoading(false);
    setReorderedStops([]);
  }, []);

  const fetchRouteInfo = () => {
    if (schedule_instance_id) {
      setLoading(true);
      const url = `/get-route-order-info/${schedule_instance_id}`;
      apiGet(url).then((json) => {
        const {
          stops = [],
          total_minutes = 0,
          total_miles = 0,
          travel_miles = 0,
          travel_minutes = 0,
          total_service_hours = 0,
          is_manual,
          Status,
          Message,
        } = json;

        if (
          !mapData?.find(
            (datum) => datum.service_vehicle_id === service_vehicle_id,
          )
        ) {
          setMapData((prev) => {
            if (prev) {
              return [
                ...prev,
                {
                  stops,
                  service_vehicle_id,
                  selected: true,
                  totalServiceHours: total_service_hours,
                  totalMinutes: total_minutes,
                },
              ];
            }
            return [
              {
                stops,
                service_vehicle_id,
                selected: true,
                totalServiceHours: total_service_hours,
                totalMinutes: total_minutes,
              },
            ];
          });
        }

        if (Status === 'Failure') {
          console.log(Message);
          reset();
        } else {
          setOrigin(stops[0]);
          setDestination(stops[stops.length - 1]);
          // We don't want to reorder the first and last stops, so we slice those out.
          setReorderedStops(stops.slice(1, -1));
          setOrgStops(stops);
          setTotalMinutes(total_minutes);
          setTotalMiles(total_miles);
          setTravelMinutes(travel_minutes);
          setTravelMiles(travel_miles);
          setTotalServiceHours(total_service_hours);
          setRouteWasSetManually(is_manual);
          setLoading(false);
        }
      });
    } else {
      reset();
    }
  };

  useEffect(fetchRouteInfo, [reset, schedule_instance_id, service_vehicle_id]);

  useEffect(() => {
    if (
      origin &&
      destination &&
      reorderedStops.length &&
      prevReorderedStops.current !== reorderedStops
    ) {
      const newMapData = mapData?.map((val) =>
        service_vehicle_id === val.service_vehicle_id
          ? {
              ...val,
              stops: [origin, ...reorderedStops, destination],
            }
          : { ...val },
      );
      setMapData(newMapData);
      prevReorderedStops.current = reorderedStops;
    }
  }, [
    destination,
    origin,
    reorderedStops,
    prevReorderedStops.current,
    service_vehicle_id,
  ]);

  const getUpdatedRouteInfo = (stops) => {
    const route_stops = [origin, ...stops, destination];
    const addIds = route_stops.map((stop) => `add_ids=${stop.address_id}`);
    const url = `/get-route-travel-info-by-address-ids?schedule_id=${schedule_instance_id}&${addIds.join(
      '&',
    )}`;
    apiGet(url).then((json) => {
      const {
        total_minutes,
        total_miles,
        travel_minutes,
        travel_miles,
        Status,
        Message,
      } = json;
      // Check to see if GET request succeeded
      if (Status === 'Failure') {
        console.log(Message);
      } else {
        setTotalMinutes(total_minutes);
        setTotalMiles(total_miles);
        setTravelMinutes(travel_minutes);
        setTravelMiles(travel_miles);
        setReOrdered(true);
      }
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    // eslint-disable-next-line no-underscore-dangle
    const _reorderedStops = reorder(
      reorderedStops,
      result.source.index,
      result.destination.index,
    );
    setReorderedStops(_reorderedStops);
    getUpdatedRouteInfo(_reorderedStops);
  };

  const handleCancel = () => {
    setReOrdered(true);
    setIsEditing(false);
    const newMapData = mapData?.map((val) =>
      service_vehicle_id === val.service_vehicle_id
        ? {
            ...val,
            stops: orgStops,
          }
        : { ...val },
    );
    setMapData(newMapData);
    setReorderedStops(reorderedStops);
  };

  const updateRouteInfo = () => {
    // Merge the start and end with the new ordering of stops
    const route_stops = [origin, ...reorderedStops, destination];
    const add_ids = route_stops.map((stop) => stop.address_id);

    apiPost('/update-route-order-info', {
      add_ids,
      si_id: schedule_instance_id,
    })
      .then(() => {
        notify(
          `Route updated successfully${
            !routeWasSetManually ? '. Route optimization disabled.' : ''
          }`,
        );
        setRouteWasSetManually(true);
        fetchRouteInfo();
      })
      .catch((error) => {
        notify(`Error: ${error}`, 'warning');
      });
  };

  const handleSave = () => {
    setIsEditing(false);
    updateRouteInfo();
  };

  const enableAutopilot = () => {
    apiGet(
      `/add-schedule-instance-id-to-route-table/${schedule_instance_id}`,
    ).then((resp) => {
      if (resp && resp.Status === 'Success') {
        notify('Route optimization enabled');
        fetchRouteInfo();
      } else {
        notify('Error: Failed to re-enable route optimization.');
      }
    });
  };

  if (loading) {
    return 'Loading...';
  }

  if (reorderedStops.length === 0) {
    return 'No route available';
  }

  const showEtas = !!getZ3PConfigValue('is_route_eta_feature_enabled');

  return (
    <>
      {showEtas && (
        <Box
          className={classes.actionSection}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.actionButton}
            startIcon={<PhoneIcon />}
            onClick={() => setShowEtaSmsModal(true)}
          >
            Send ETAs
          </Button>
        </Box>
      )}
      <EtaSmsModal
        open={showEtaSmsModal}
        setOpen={setShowEtaSmsModal}
        stops={orgStops}
      />
      <Box
        className={classes.actionSection}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {!isEditing && (
          <>
            <Typography variant="h6">{service_vehicle_name}</Typography>
            <Tooltip
              title={`Reorder route stops${
                !routeWasSetManually
                  ? '. This will disable route optimization.'
                  : ''
              }`}
            >
              <IconButton onClick={toggleIsEditing}>
                <LowPriorityIcon style={classes.toggleButton} />
              </IconButton>
            </Tooltip>
            {routeWasSetManually && (
              <Tooltip title="Re-enable route optimization: Allow this route to automatically optimize itself">
                <IconButton onClick={enableAutopilot}>
                  <DepartureBoardIcon style={classes.toggleButton} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
        {isEditing && (
          <Box textAlign="center" mt={2} mb={1} width="100%">
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
                className={classes.actionButton}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.actionButton}
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          </Box>
        )}
      </Box>
      <Box className="serviceAccordion">
        <Box className={classes.serviceWrapper}>
          <Box className={classes.fullWidth}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    <DragContainer
                      stop={origin}
                      minutes={travelMinutes[0]}
                      miles={travelMiles[0]}
                    />
                    {isEditing && (
                      <ContainerFooter
                        minutes={travelMinutes[0]}
                        miles={travelMiles[0]}
                      />
                    )}

                    {reorderedStops?.map((stop, i) => (
                      <>
                        <Draggable
                          key={stop.address_id}
                          draggableId={stop.address_id.toString()}
                          index={i}
                          isDragDisabled={!isEditing}
                        >
                          {(draggable_provided) => (
                            <div
                              ref={draggable_provided.innerRef}
                              {...draggable_provided.draggableProps}
                            >
                              <DragContainer
                                isEditing={isEditing}
                                i={i}
                                stop={stop}
                                minutes={travelMinutes[i + 1]}
                                miles={travelMiles[i + 1]}
                                drag={draggable_provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                        {isEditing && (
                          <ContainerFooter
                            minutes={travelMinutes[i + 1]}
                            miles={travelMiles[i + 1]}
                          />
                        )}
                      </>
                    ))}
                    <DragContainer stop={destination} lastItem />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DispatchVehicle;
